import React, { useEffect, useRef, useState } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';

// Sample data for the network graph
const generateData = () => {
  const networkTypes = ['5g', 'fiber', 'cloud', 'hybrid', 'satcom', 'radio', 'broadband'];
  const data = [];
  
  for (const type of networkTypes) {
    // Generate a value between 50 and 200
    const value = Math.floor(Math.random() * 150) + 50;
    
    data.push({
      name: type.toUpperCase(),
      value,
      // For visual variety, assign different categories
      category: ['Signal', 'Traffic', 'Bandwidth'][Math.floor(Math.random() * 3)]
    });
  }
  
  return data;
};

// Get color based on network type
const getNetworkColor = (type: string, isDark: boolean) => {
  const colors = {
    '5G': isDark ? '#FF6600' : '#FF8C00',
    'FIBER': isDark ? '#00D25B' : '#00A550',
    'CLOUD': isDark ? '#6366F1' : '#4F46E5',
    'HYBRID': isDark ? '#F472B6' : '#EC4899',
    'SATCOM': isDark ? '#FBBF24' : '#F59E0B',
    'RADIO': isDark ? '#5EEAD4' : '#14B8A6',
    'BROADBAND': isDark ? '#C084FC' : '#A855F7',
    'DEFAULT': isDark ? '#9CA3AF' : '#6B7280'
  };
  
  return colors[type] || colors.DEFAULT;
};

interface NetworkGraphProps {
  className?: string;
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({ className }) => {
  const [data, setData] = useState(generateData());
  const intervalRef = useRef<number | null>(null);
  const { resolvedTheme } = useTheme();
  const isMobile = useMobile();
  const isDark = resolvedTheme === 'dark';
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // Generate time series data for line chart
  const generateTimeSeriesData = () => {
    // Create 7 time points
    const timePoints = Array.from({ length: 7 }, (_, i) => {
      const time = new Date();
      time.setMinutes(time.getMinutes() - (6 - i) * 5); // 5 minute intervals
      return time.getHours() + ':' + (time.getMinutes() < 10 ? '0' : '') + time.getMinutes();
    });
    
    // Generate data for each network type
    const networkTypes = ['5G', 'FIBER', 'CLOUD', 'HYBRID', 'SATCOM', 'RADIO', 'BROADBAND'];
    
    return timePoints.map(time => {
      const dataPoint: any = { time };
      
      networkTypes.forEach(type => {
        // Random value between 40 and 100 with some consistency
        const lastValue = dataPoint[type] || (Math.random() * 60 + 40);
        const change = (Math.random() - 0.5) * 15; // Random change
        dataPoint[type] = Math.max(30, Math.min(120, lastValue + change));
      });
      
      return dataPoint;
    });
  };
  
  const [timeSeriesData, setTimeSeriesData] = useState(generateTimeSeriesData());
  
  // Update data more frequently for simulation
  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      // For bar data
      setData(generateData());
      
      // For line data - add new point and remove oldest
      setTimeSeriesData(prev => {
        const newData = [...prev];
        // Remove first item
        newData.shift();
        
        // Add new data point
        const time = new Date();
        const timeString = time.getHours() + ':' + (time.getMinutes() < 10 ? '0' : '') + time.getMinutes();
        
        const lastPoint = newData[newData.length - 1];
        const newPoint: any = { time: timeString };
        
        // Generate new values for each network with some continuity from last point
        Object.keys(lastPoint).forEach(key => {
          if (key !== 'time') {
            const lastValue = lastPoint[key];
            const change = (Math.random() - 0.5) * 15;
            newPoint[key] = Math.max(30, Math.min(120, lastValue + change));
          }
        });
        
        newData.push(newPoint);
        return newData;
      });
    }, 2000); // Reduced from 5000ms to 2000ms for faster updates
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
        <LineChart 
          data={timeSeriesData} 
          margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 5 } : { top: 5, right: 20, left: 0, bottom: 30 }}
        >
          <XAxis 
            dataKey="time" 
            tick={{ fontSize: isMobile ? 10 : 12, fill: isDark ? '#f0f0f0' : '#333333' }} 
            axisLine={{ stroke: isDark ? '#444' : '#ccc' }} 
            tickLine={{ stroke: isDark ? '#444' : '#ccc' }}
          />
          <YAxis 
            tick={{ fontSize: isMobile ? 10 : 12, fill: isDark ? '#f0f0f0' : '#333333' }} 
            axisLine={{ stroke: isDark ? '#444' : '#ccc' }} 
            tickLine={{ stroke: isDark ? '#444' : '#ccc' }}
            width={30}
            domain={[0, 120]}
          />
          <Tooltip 
            contentStyle={{ 
              background: isDark ? 'rgba(51, 51, 51, 0.8)' : 'rgba(255, 255, 255, 0.8)', 
              border: 'none', 
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              color: isDark ? '#f0f0f0' : '#333333',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              padding: isMobile ? '4px 8px' : '8px 12px'
            }}
            formatter={(value: any, name: string) => {
              if (name !== 'time') {
                return [typeof value === 'number' ? value.toFixed(1) : value, name];
              }
              return [value, name];
            }}
            labelFormatter={(label) => `Time: ${label}`}
          />
          
          {/* Generate a line for each network type */}
          {['5G', 'FIBER', 'CLOUD', 'HYBRID', 'SATCOM', 'RADIO', 'BROADBAND'].map((network) => (
            <Line
              key={network}
              type="monotone"
              dataKey={network}
              stroke={getNetworkColor(network, isDark)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              animationDuration={500}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default NetworkGraph;
